import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { List, Map } from 'immutable';
import { omit } from 'lodash';
import PropTypes from 'prop-types';

import ClickAwayWrapper from 'components/ClickAwayWrapper';
import FieldRenderer from 'components/forms/FieldRenderer';

import { updateFormField } from 'util/forms';

export default (formName, { getFieldValue, getFieldErrors }) => {
  @connect(
    (state, props) => ({
      value: getFieldValue(state, props),
      errors: getFieldErrors(state, props),
    }),
    { updateField: updateFormField(formName) }
  )
  class FormField extends PureComponent {
    static propTypes = {
      component: PropTypes.func,
      fieldName: PropTypes.string.isRequired,
      errors: PropTypes.instanceOf(List),
      value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
      ]),
      validate: PropTypes.func,
      validateOnMount: PropTypes.bool,
      onFocus: PropTypes.func,
      onBlur: PropTypes.func,
      touchOnClickOutside: PropTypes.bool,
      disabled: PropTypes.bool,
      formatOnChangeValue: PropTypes.func,
      // Signature: (fieldName, payload={ value, errors }, updateField)
      customUpdateField: PropTypes.func,
    };

    static defaultProps = {
      component: FieldRenderer,
      fieldData: Map(),
      formatOnChangeValue: val => val,
    };

    constructor() {
      super();

      this.state = {
        touched: false,
      };
    }

    componentDidMount() {
      if (this.props.validateOnMount) {
        this.handleChange(this.props.value);
      }
    }

    handleClickOutside = () =>
      this.props.touchOnClickOutside &&
      !this.state.touched &&
      this.setState({ touched: true });

    handleBlur = value => {
      if (!this.state.touched) {
        this.setState({ touched: true });
      }
      if (this.props.onBlur) {
        this.props.onBlur(value);
      }
    };

    handleChange = value => {
      const formattedValue = this.props.formatOnChangeValue(value);
      const validationResponse =
        this.props.validate && this.props.validate(formattedValue);
      const errors = validationResponse ? List([validationResponse]) : List();

      const changeHandler =
        this.props.customUpdateField || this.props.updateField;

      changeHandler(
        this.props.fieldName,
        { value: formattedValue, errors },
        this.props.updateField
      );
    };

    buildComponentProps = props => {
      const { fieldName, value, disabled, onFocus, ...otherProps } = props;
      const { touched } = this.state;

      return {
        input: {
          name: fieldName,
          value,
          disabled,
          onChange: this.handleChange,
          onBlur: this.handleBlur,
          onFocus,
        },
        touched,
        ...omit(otherProps, ['updateField', 'validate', 'onBlur']),
      };
    };

    render() {
      const { component, ...props } = this.props;
      const componentProps = this.buildComponentProps(props);

      return (
        <ClickAwayWrapper onClickAway={this.handleClickOutside}>
          {React.createElement(component, componentProps)}
        </ClickAwayWrapper>
      );
    }
  }

  return FormField;
};
